import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, Phone, X } from "lucide-react";
import { api } from "@/lib/api";
import { BreathCircle } from "@/components/BreathCircle";
import { DEFAULT_SAFETY_PLAN } from "@/data/safety";
import { cachePlan, readCachedPlan } from "@/pages/Safety";

const FALLBACK = {
  warningSigns: ["Feeling hopeless or trapped", "Pulling away from people", "Thoughts racing or going dark"],
  internalStrategies: ["Hold something cold", "Put on a song I love", "Step outside for fresh air", "Take a warm shower"],
  safeEnvironmentSteps: ["Move anything I could hurt myself with out of reach", "Go to a room where other people are"],
  reasonsToLive: ["The people who love me", "Things I still want to see and do", "Feelings change, even this one"],
};
const PHONE = /(\+?\d[\d\s().-]{6,}\d)/;
const pinnedLine = () => { try { return JSON.parse(localStorage.getItem("mm_crisis_pin_line") || "null"); } catch { return null; } };
const big = "w-full rounded-2xl py-5 px-5 text-lg font-semibold text-left flex items-center gap-3";

const usePlan = () => {
  const [plan, setPlan] = useState(() => ({ ...DEFAULT_SAFETY_PLAN, ...readCachedPlan()?.plan }));
  useEffect(() => { api.get("/safety/full-plan").then(({ data }) => { const p = { ...DEFAULT_SAFETY_PLAN, ...data.plan }; setPlan(p); cachePlan(p); }).catch(() => {}); }, []);
  return plan;
};

// Swap in the "My Safety Plan" manifest so Add to Home Screen creates its own icon
const useSafetyManifest = () => useEffect(() => {
  const link = document.querySelector('link[rel="manifest"]');
  const prev = link?.getAttribute("href");
  link?.setAttribute("href", "/safety-manifest.json");
  return () => { if (link && prev) link.setAttribute("href", prev); };
}, []);

const Contact = ({ text, testId }) => {
  const num = text.match(PHONE)?.[1]?.replace(/[^\d+]/g, "");
  return (
    <div className="rounded-2xl bg-white/10 p-4" data-testid={testId}>
      <div className="text-lg font-semibold break-words">{text}</div>
      {num && <div className="flex gap-2 mt-3">
        <a href={`tel:${num}`} className="flex-1 rounded-full bg-white text-[#1B1A33] font-bold py-3.5 flex items-center justify-center gap-2" data-testid={`${testId}-call`}><Phone size={18} />Call</a>
        <a href={`sms:${num}`} className="flex-1 rounded-full bg-white/20 font-bold py-3.5 flex items-center justify-center gap-2" data-testid={`${testId}-text`}><MessageSquare size={18} />Text</a>
      </div>}
    </div>
  );
};

const CrisisButtons = () => {
  const pin = pinnedLine();
  return (
    <div className="space-y-3" data-testid="walk-crisis-lines">
      {pin?.call && pin.call !== "988" && <a href={`tel:${pin.call}`} className={`${big} bg-white text-[#1B1A33]`} data-testid="walk-call-pinned"><Phone size={22} />Call {pin.name}</a>}
      <a href="tel:988" className={`${big} bg-white text-[#C2185B]`} data-testid="walk-call-988"><Phone size={22} />Call 988 Lifeline</a>
      <a href="sms:988" className={`${big} bg-white/15`} data-testid="walk-text-988"><MessageSquare size={22} />Text 988</a>
      <a href="tel:911" className={`${big} bg-[#C2185B]`} data-testid="walk-call-911"><Phone size={22} />In danger now? Call 911</a>
    </div>
  );
};

const List = ({ items, testId, own }) => (
  <div className="space-y-3" data-testid={testId}>
    {!own && <p className="text-sm opacity-70">You haven't added your own yet. Here are some ideas.</p>}
    {items.map((t, i) => <div key={`${t}-${i}`} className="rounded-2xl bg-white/10 p-4 text-lg" data-testid={`${testId}-${i}`}>{t}</div>)}
  </div>
);

const pick = (plan, keys, fallbackKey) => {
  const own = keys.flatMap((k) => plan[k] || []);
  return { items: own.length ? own : FALLBACK[fallbackKey] || [], own: own.length > 0 };
};

const Steps = (plan, setPicked, picked) => [
  { title: "Breathe with me", body: <BreathCircle seconds={30} color="#B9A6F0" testId="walk-breathe" /> },
  { title: "My warning signs", q: "Is this what's happening?", body: <List testId="walk-signs" {...pick(plan, ["warningSigns", "emotionalWarningSigns", "physicalWarningSigns", "behavioralWarningSigns", "situationalWarningSigns"], "warningSigns")} /> },
  { title: "Things I can do on my own", q: "Tap one and start it now.", body: (() => {
    const { items, own } = pick(plan, ["internalStrategies"], "internalStrategies");
    return <div className="space-y-3" data-testid="walk-strategies">{!own && <p className="text-sm opacity-70">Ideas until you add your own:</p>}
      {items.map((t, i) => <button key={`${t}-${i}`} onClick={() => setPicked(t)} className={`${big} ${picked === t ? "bg-[#B9A6F0] text-[#1B1A33]" : "bg-white/10"}`} data-testid={`walk-strategy-${i}`}>{picked === t ? "▶ Doing now: " : ""}{t}</button>)}
      <Link to="/sos" className="block text-center underline opacity-80 py-2" data-testid="walk-sos-link">Or try SOS grounding</Link></div>;
  })() },
  { title: "People and places that help", body: (() => {
    const people = [...(plan.trustedContacts || []), ...(plan.socialDistractors || [])];
    const places = plan.safePlaces || [];
    return <div className="space-y-3" data-testid="walk-people">{people.map((t, i) => <Contact key={`${t}-${i}`} text={t} testId={`walk-person-${i}`} />)}
      {places.map((t, i) => <div key={`${t}-${i}`} className="rounded-2xl bg-white/10 p-4 text-lg" data-testid={`walk-place-${i}`}>📍 {t}</div>)}
      {!people.length && !places.length && <p className="opacity-80" data-testid="walk-people-empty">You haven't added people yet. Think of one person you could text "I'm having a hard time". You can add them to your plan later.</p>}</div>;
  })() },
  { title: "Professionals and crisis lines", body: <div className="space-y-3">{(plan.professionalContacts || []).map((t, i) => <Contact key={`${t}-${i}`} text={t} testId={`walk-pro-${i}`} />)}<CrisisButtons /></div> },
  { title: "Making my space safe", body: <List testId="walk-safe-space" {...pick(plan, ["safeEnvironmentSteps", "meansReduction"], "safeEnvironmentSteps")} /> },
  { title: "My reasons to live", body: (() => {
    const { items, own } = pick(plan, ["reasonsToLive"], "reasonsToLive");
    return <div className="space-y-4" data-testid="walk-reasons">{!own && <p className="text-sm opacity-70">Until you add your own:</p>}{items.map((t, i) => <div key={`${t}-${i}`} className="text-2xl sm:text-3xl font-semibold leading-snug" data-testid={`walk-reason-${i}`}>💜 {t}</div>)}</div>;
  })() },
];

const Check = ({ onSafe, onNot }) => (
  <div className="space-y-3" data-testid="walk-check">
    <h2 className="text-3xl font-semibold mb-4">How are you now?</h2>
    <button onClick={onSafe} className={`${big} bg-white text-[#1B1A33] justify-center`} data-testid="walk-safer">I'm a bit safer now</button>
    <button onClick={onNot} className={`${big} bg-[#C2185B] justify-center`} data-testid="walk-not-safe">I'm still not safe</button>
  </div>
);

export default function SafetyWalkthrough() {
  const nav = useNavigate();
  useSafetyManifest();
  const plan = usePlan();
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [end, setEnd] = useState(null);
  const steps = Steps(plan, setPicked, picked);
  const s = steps[i];
  const done = i >= steps.length;
  const exit = () => (window.history.length > 1 ? nav(-1) : nav("/"));
  useEffect(() => { window.scrollTo(0, 0); }, [i, end]);
  return (
    <div className="min-h-screen bg-[#1B1A33] text-white flex flex-col" data-testid="safety-walkthrough">
      <div className="flex justify-between items-center p-4">
        <span className="text-sm opacity-70" data-testid="walk-progress">{done ? "Check-in" : `Step ${i + 1} of ${steps.length}`}</span>
        <button onClick={exit} className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center" aria-label="Leave safety plan" data-testid="walk-exit"><X size={20} /></button>
      </div>
      <div className="flex-1 w-full max-w-lg mx-auto px-6 pb-8" key={`${i}-${end}`}>
        {!done && <><h1 className="text-3xl sm:text-4xl font-semibold mb-2" data-testid="walk-title">{s.title}</h1>{s.q && <p className="text-lg opacity-80 mb-5">{s.q}</p>}<div className="mt-4">{s.body}</div></>}
        {done && !end && <Check onSafe={() => setEnd("safe")} onNot={() => setEnd("not")} />}
        {end === "safe" && <div data-testid="walk-end-safe"><h2 className="text-3xl font-semibold mb-3">Thank you for using your plan 💜</h2><p className="text-lg opacity-80 mb-6">That took courage. If things get hard again, your plan is always one tap away, even offline.</p><Link to="/" className={`${big} bg-white text-[#1B1A33] justify-center`} data-testid="walk-home">Back to Home</Link></div>}
        {end === "not" && <div data-testid="walk-end-not"><h2 className="text-3xl font-semibold mb-3">Please reach out right now</h2><p className="text-lg opacity-80 mb-6">You deserve support. Someone is ready to listen, any time of day.</p><CrisisButtons /></div>}
      </div>
      {!done && (
        <div className="flex gap-3 w-full max-w-lg mx-auto px-6 pb-10">
          {i > 0 && <button onClick={() => setI(i - 1)} className="flex-1 rounded-full bg-white/10 font-semibold py-4 text-lg" data-testid="walk-back">Back</button>}
          <button onClick={() => setI(i + 1)} className="flex-[2] rounded-full bg-[#B9A6F0] text-[#1B1A33] font-bold py-4 text-lg" data-testid="walk-next">{i === 1 ? "Yes, keep going" : "Next"}</button>
        </div>
      )}
    </div>
  );
}
